import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Col, Row, Container } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import { MdSupervisedUserCircle, MdAccountCircle, MdPlace } from "react-icons/md";
import { BsClockFill, BsFillCalendarEventFill, BsCalendarEvent } from "react-icons/bs";
import Lead from "../../shared/Lead";
import * as API from "../../api";
import TextArea from "../../shared/TextArea";
import Patient from "../../shared/Patient";
import SubmitButton from "../../shared/SubmitButton";
import SelectAssignee from "../forms/SelectAssignee";
import SelectPcp from "../forms/SelectPcp";
import SelectVisitType from "../forms/SelectVisitType";
import Select from "../../shared/Select";
import FieldError from "../../shared/FieldError";

import { Settings } from "../../helpers/Contexts";
import * as Utils from "../../helpers/Utility";

const AppointmentScheduledForm = ({ dispatch, state, patient, isCall, formHandler }) => {
  const [error, setError] = useState(null);
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const settings = useContext(Settings);

  const ScheduleSchema = Yup.object().shape({
    appointment_source: Yup.string().required("Appointment source is required"),
    appointment_initiated_date: Yup.date().required("Date appointment was initiated is required"),
    appointment_time: Yup.string().matches(
      "([01]?[0-9]|2[0-3]):[0-5][0-9]",
      "Appointment time must be in a valid 12 hour (e.g HH:MM AM/PM) or military (e.g. 13:00) format. "
    ),
    appointment_date: Yup.date().required("Appointment date is required."),
    assignee: Yup.string().required(
      "Assignee is required. Assign this task to either a suppport group or agent."
    ),
    procedure_type: Yup.string().required("Visit type is required"),
    pcp: Yup.string().nullable(),
  });

  return (
    <>
      <Formik
        initialValues={{
          appointment_source: "",
          appointment_initiated_date: "",
          order_date: "",
          notes: "",
          procedure_type_id: "",
          procedure_type: "",
          pcp: "",
          pcp_id: "",
          pcp_practice_name: "",
          pcp_practice_id: "",
          pcp_practice_address: "",
          assigner_id: settings.me.id,
          assignee_id: "",
          assignee_support_group_id: "",
          assignee: "",
          appointment_date: "",
          appointment_time: "",
          date: "",
        }}
        validationSchema={ScheduleSchema}
        validateOnChange={true}
        onSubmit={(values) => {
          setLoading(true);
          const task = {
            status: "open",
            task_type: "appointment_request",
            assigner_id: settings.me.id,
            assigner_support_group_id: settings.me.support_group_id,
            appointment_source: values.appointment_source,
            order_date: values.order_date,
            assignee_id: values.assignee_id,
            patient_id: state.create.patient.id,
            assignee_support_group_id: values.assignee_support_group_id,
            appointment_time: Utils.ConvertTime(values.appointment_time),
            appointment_request_status: "appointment_confirmed",
            appointment_date: values.appointment_date,
            procedure_type_id: values.procedure_type_id,
            servicing_provider: values.pcp,
            pcp_practice_id: values.pcp_practice_id,
            notes: values.notes,
          };
          if (isCall) {
            formHandler({
              task: {
                id: state.currentTask.id,
                ...task,
              },
            });
          } else {
            API.postTask({
              task: {
                ...task,
              },
            })
              .then((res) => {
                setLoading(false);
                setTaskSubmitted(true);
                dispatch({ type: "set_loading", payload: true });
                history.push(`/physicians/tasks/item/${res.task.id}`);
              })
              .catch((err) => {
                setError(err.error);
              });
          }
        }}
      >
        {({ values, setFieldValue, errors, isSubmitting }) => {
          return (
            <Form>
              { error && (
                <>
                  <Row className="flex red-text">
                    <Col xs={12}>
                      The server returned an error: <br />
                      <b>{ error }</b>
                    </Col>
                  </Row>
                </>
              )}
              <Row className="flex">
                <Col md={5} className="form">
                  <br />
                  <Lead message={isCall ? "Appointment Scheduled" : "New Appointment Scheduled"} />
                  <br /> <br />
                  <SelectAssignee name="assignee" setFieldValue={setFieldValue} />
                  <Row>
                    <Col md={6} id="field-left">
                      <label>{"Appointment Initiated Date"}</label>

                      <div className={"flex datefield-wrapper"}>
                        <Field name="appointment_initiated_date">
                          {({ field, meta }) => {
                            return (
                              <>
                                {" "}
                                <BsFillCalendarEventFill className="calendar-icon" />
                                <ReactDatePicker
                                  autoComplete="off"
                                  {...field}
                                  dateFromat={"YYYY-MM-dd"}
                                  className="datepicker"
                                  onChange={(date) => {
                                    setFieldValue(
                                      "appointment_initiated_date",
                                      date.toISOString().slice(0, 10)
                                    );
                                    setFieldValue("order_date", date);
                                  }}
                                />
                              </>
                            );
                          }}
                        </Field>
                      </div>
                    </Col>
                    <Col md={6} id="field-right">
                      <label>Appointment Source</label>
                      <Select name="appointment_source">
                        <option value={""}>{"Select appointment source"}</option>
                        {settings.appointment_sources.map((option, i) => (
                          <option key={i} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                  <SelectVisitType name="procedure_type" setFieldValue={setFieldValue} />
                  <label>{"Servicing Provider"}</label>
                  {values.procedure_type == "Lab/Diagnostic Only" ? (
                    <select disabled className="dropdown-select">
                      <option value={""}>{"N/A for Lab/Diagnostic Only"}</option>
                    </select>
                  ) : (
                    <SelectPcp name="pcp" required={true} setFieldValue={setFieldValue} />
                  )}
                  <label>Appointment Date & Time</label>
                  <div className="flex">
                    <div className={"flex datefield-wrapper"}>
                      <Field name="date">
                        {({ field, meta }) => {
                          return (
                            <>
                              <BsFillCalendarEventFill className="calendar-icon" />
                              <ReactDatePicker
                                autoComplete="off"
                                {...field}
                                className="datepicker"
                                dateFromat={"YYYY-MM-dd"}
                                onChange={(date) => {
                                  setFieldValue("appointment_date", date);
                                  setFieldValue("date", date.toISOString().slice(0, 10));
                                }}
                              />
                            </>
                          );
                        }}
                      </Field>
                    </div>
                    <div className={"flex datefield-wrapper"}>
                      <BsClockFill className="calendar-icon" />
                      <Field name="appointment_time" className="datepicker" />
                    </div>
                  </div>
                  <div className="mt10">
                    <Col md={6} id="field-left">
                      {errors.appointment_date ? (
                        <FieldError error={errors.appointment_date} />
                      ) : null}
                    </Col>
                    <Col md={6} id="field-right">
                      {errors.appointment_time ? (
                        <FieldError error={errors.appointment_time} />
                      ) : null}
                    </Col>
                  </div>
                  <TextArea label="Notes" name="notes" placeholder="Write additional notes" />
                  <div className="form-button mt20-bt">
                    <button id="btn outline" type="submit" disabled={isSubmitting}>
                      Submit Appointment Request
                    </button>
                  </div>
                </Col>

                <Col md={7} className={"pad20 border-container"}>
                  <Patient patient={{ ...state.create.patient }} />
                  <hr />
                  <label className="agent">
                    {"Assignee "}{" "}
                    <span id="from">
                      {values.assignee && (
                        <>
                          <MdAccountCircle className="task-agent" />
                          <b> {values.assignee}</b>
                        </>
                      )}
                    </span>
                  </label>
                  <hr />
                  {!isCall && <Lead message={state.create.type} />}
                  <br />
                  <BsCalendarEvent id="appointment-big-icon" />
                  <label>{"Visit Type"}</label>
                  {values.procedure_type}
                  <label>{"Servicing Provider"}</label>
                  {values.procedure_type == "Lab/Diagnostic Only"
                    ? "N/A for Lab/Diagnostic Only"
                    : values.pcp}
                  {values.pcp_practice_id && (
                    <>
                      <label>Location</label>
                      <MdPlace className="pcp-icon" />
                      {values.pcp_practice_name}
                      <br />
                      {values.pcp_practice_address}
                    </>
                  )}{" "}
                  <br />
                  <div className="mt10 mb10">
                    <label>Appointment Date & Time </label>
                    {values.date && (
                      <>
                        <BsFillCalendarEventFill className="cpr-label-icon" />
                        <b>{" " + values.date + " "}</b>
                      </>
                    )}
                    {values.appointment_time && (
                      <>
                        <BsClockFill className="cpr-label-icon" />
                        <b>{values.appointment_time} </b>
                        {"  "}(Pacific Standard/Daylight Time)
                      </>
                    )}
                  </div>
                  <label>Notes</label>
                  {values.notes}
                  <label>{"Appointment Initated Date"}</label>
                  {values.appointment_initiated_date}
                  <label>{"Appointment Source"}</label>
                  {values.appointment_source &&
                    values.appointment_source
                      .replaceAll("_", " ")
                      .replaceAll("-", " ")
                      .toUpperCase()}
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default AppointmentScheduledForm;
